import React, { useState, useEffect, useContext, useCallback } from "react";
import { Container, Card, Modal } from "react-bootstrap";
import {
  FiTruck,
  FiClock,
  FiCheckCircle,
  FiPackage,
  FiTag,
  FiXCircle,
  FiUser,
  FiDollarSign,
  FiCreditCard,
  FiPhone,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import UserMasterLayout from "../../layouts/CustomerMasterLayout";
import { SkeletonBlock } from "../../components/general/skeleton/SkeletonBlock";
import Button from "../../components/general/button/Button";
import MyContext from "../../context/MyContext";
import "../../assets/css/customer/customerDashboard/orderBids/CustomerOrderBids.css";

const CustomerOrderBids = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, loggedInCustomer } = useContext(MyContext);

  // ── order id location.state se ya localStorage fallback se ──
  const kmorder_id =
    location.state?.kmorder_id || localStorage.getItem("pendingOrderBidsId");

  const [job, setJob] = useState(null);
  const [order, setOrder] = useState(null);
  const [bids, setBids] = useState([]);
  const [selectedBid, setSelectedBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // ── payment modal + Pay Now loading state ──
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  // ── driver select ho chuka hai (job "open" se aage nikal chuka) ──
  const driverSelected = job != null && job.status !== "open";

  const{authToken} = useContext(MyContext);
  // ── jaise hi driver select ho (ya reload par pata chale ke pehle
  // se select ho chuka hai), summary modal khud khul jaye taake
  // customer seedha payment ki taraf guide ho ──
  useEffect(() => {
    if (driverSelected) setShowSummaryModal(true);
  }, [driverSelected]);

  // ── viewJobBids — job + order + pending bids + selected bid fetch karta hai ──
  const viewJobBids = useCallback(() => {
    if (!kmorder_id) return;
    const formData = new FormData();
    formData.append("kmorder_id", kmorder_id);

    axios
      .post(`${baseUrl}/customer/jobbids/viewJobBids`, formData, {
        headers: {Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          // job/order mil chuka process ho chuka ho sakta hai — koi error show na karain, bas empty list
          setBids([]);
          return;
        }
        setJob(response.data.job);
        setOrder(response.data.order);
        setBids(response.data.bids || []);
        setSelectedBid(response.data.selectedBid || null);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [baseUrl, kmorder_id]);

  useEffect(() => {
    if (!kmorder_id) {
      Swal.fire({
        icon: "error",
        title: "No Order Found",
        text: "Please place an order first.",
      }).then(() => navigate("/customer/cart"));
      return;
    }

    localStorage.setItem("pendingOrderBidsId", kmorder_id);
    viewJobBids();

    const interval = setInterval(viewJobBids, 8000);
    return () => clearInterval(interval);
  }, [kmorder_id, navigate, viewJobBids]);

  // ── selectDriverBid — driver select karo, phir agar payment chahiye to summary modal khulega ──
  const selectDriverBid = (bid) => {
    setSelectingId(bid.id);
    const formData = new FormData();
    formData.append("kmdeliveryjobbid_id", bid.id);

    axios
      .post(`${baseUrl}/customer/jobbids/selectDriverBid`, formData, {
        headers: {Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setSelectingId(null);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              (response.data.error || response.data.message || "Something went wrong.") +
              " Please try again.",
          }).then(() => {
            localStorage.removeItem("pendingOrderBidsId");
            navigate("/customer/cart");
          });
          return;
        }

        // ── Case 1: payment se koi taluq nahi tha (dropout reselection) ──
        if (response.data.message?.toLowerCase().includes("no additional payment")) {
          Swal.fire({
            icon: "success",
            title: "Driver Assigned!",
            text: response.data.message,
            confirmButtonColor: "#097d43",
          }).then(() => viewJobBids());
          return;
        }

        // ── Case 2: pehli dafa selection — job refresh karo, ab
        // driverSelected true ho jayega aur summary modal khud khul jayega ──
        viewJobBids();
      })
      .catch(() => {
        setSelectingId(null);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to select driver. Please try again.",
        }).then(() => {
          localStorage.removeItem("pendingOrderBidsId");
          navigate("/customer/cart");
        });
      });
  };

  // ══════════════════════════════════════════════════════════
  // payForOrder — Stripe session banata hai aur redirect kar deta hai.
  // Agar payment shuru karte waqt (ya Stripe session banate waqt)
  // koi error aa jaye, to backend order ko khud handle kar chuka
  // hota hai (paid hai to chhod deta hai, warna delete kar deta
  // hai — see cleanupFailedOrder). Frontend sirf message dikha
  // ke Orders page pe le jata hai taake customer current sahi
  // state dekh sake.
  // ══════════════════════════════════════════════════════════
  const payForOrder = () => {
    setPayLoading(true);
    const formData = new FormData();
    formData.append("kmorder_id", kmorder_id);

    axios
      .post(`${baseUrl}/customer/order/payForOrder`, formData, {
        headers: {Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          setPayLoading(false);
          Swal.fire({
            icon: "error",
            title: "Payment Failed",
            text:
              response.data.error ||
              response.data.message ||
              "Something went wrong while starting payment. Please try again.",
          }).then(() => {
            localStorage.removeItem("pendingOrderBidsId");
            navigate("/customer/orders");
          });
          return;
        }
        if (response.data.url) {
          localStorage.removeItem("pendingOrderBidsId");
          window.location.href = response.data.url;
        }
      })
      .catch(() => {
        setPayLoading(false);
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: "Failed to start payment. Please try again.",
        }).then(() => {
          localStorage.removeItem("pendingOrderBidsId");
          navigate("/customer/orders");
        });
      });
  };

  // NEW: Bank Transfer Payment (Module 3) — Stripe ka alternative,
  // Admin verify karta hai, turant confirm nahi hota.
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);
  const [adminBankAccount, setAdminBankAccount] = useState(null);
  const [loadingBankAccount, setLoadingBankAccount] = useState(false);
  const [transferredAmount, setTransferredAmount] = useState("");
  const [receiptImage, setReceiptImage] = useState(null);
  const [submittingReceipt, setSubmittingReceipt] = useState(false);

  const openBankTransferModal = () => {
    setShowBankTransferModal(true);
    setLoadingBankAccount(true);
    axios
      .get(`${baseUrl}/customer/order/viewAdminBankAccountForPayment`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setLoadingBankAccount(false);
        if (res.data.success) {
          setAdminBankAccount(res.data.account);
        } else {
          Swal.fire({ icon: "error", title: "Not Available", text: res.data.message });
          setShowBankTransferModal(false);
        }
      })
      .catch(() => {
        setLoadingBankAccount(false);
        setShowBankTransferModal(false);
      });
  };

  const submitBankTransferReceipt = () => {
    if (!transferredAmount || Number(transferredAmount) <= 0) {
      Swal.fire({ icon: "warning", title: "Amount Required", text: "Please enter the amount you transferred." });
      return;
    }
    if (!receiptImage) {
      Swal.fire({ icon: "warning", title: "Receipt Required", text: "Please upload your bank transfer receipt." });
      return;
    }

    setSubmittingReceipt(true);
    const formData = new FormData();
    formData.append("kmorder_id", kmorder_id);
    formData.append("transferred_amount", transferredAmount);
    formData.append("transfer_receipt_image", receiptImage);

    axios
      .post(`${baseUrl}/customer/order/submitBankTransferReceipt`, formData, {
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setSubmittingReceipt(false);
        if (response.data.success === false) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message });
          return;
        }
        setShowBankTransferModal(false);
        Swal.fire({
          icon: "success",
          title: "Receipt Submitted",
          text: "Your order will be confirmed once Admin verifies your payment.",
        }).then(() => {
          localStorage.removeItem("pendingOrderBidsId");
          navigate("/customer/orders");
        });
      })
      .catch(() => {
        setSubmittingReceipt(false);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to submit receipt. Please try again." });
      });
  };


  // Agar order already "Paid" hai to refund ke liye bank details
  // mangte hain (backend cancelOrder isi tarah expect karta hai);
  // warna seedha cancel kar dete hain.
  // ══════════════════════════════════════════════════════════
  const handleCancelOrder = async () => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Cancel Order?",
      text: "Are you sure you want to cancel your order?",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel Order",
      cancelButtonText: "No",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#097d43",
    });

    if (!confirm.isConfirmed) return;

    let bankFields = {};

    if (order?.paymentStatus === "Paid") {
      const { value: formValues } = await Swal.fire({
        title: "Refund Bank Details",
        text: "Payment has already been made — please enter your bank details to receive the refund.",
        html:
          '<input id="swal-bank-name" class="swal2-input" placeholder="Bank Name">' +
          '<input id="swal-account-title" class="swal2-input" placeholder="Account Title">' +
          '<input id="swal-account-number" class="swal2-input" placeholder="Account Number">',
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Submit",
        confirmButtonColor: "#097d43",
        preConfirm: () => {
          const bankName = document.getElementById("swal-bank-name").value.trim();
          const accountTitle = document.getElementById("swal-account-title").value.trim();
          const accountNumber = document.getElementById("swal-account-number").value.trim();
          if (!bankName || !accountTitle || !accountNumber) {
            Swal.showValidationMessage("All bank details are required for the refund");
            return false;
          }
          return { bankName, accountTitle, accountNumber };
        },
      });

      if (!formValues) return;

      bankFields = {
        refund_bank_name: formValues.bankName,
        refund_account_title: formValues.accountTitle,
        refund_account_number: formValues.accountNumber,
      };
    }

    setCancelling(true);
    const formData = new FormData();
    formData.append("kmorder_id", kmorder_id);
    Object.entries(bankFields).forEach(([key, value]) => formData.append(key, value));

    axios
      .post(`${baseUrl}/customer/order/cancelOrder`, formData, {
        headers: {Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setCancelling(false);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || response.data.message,
          });
          return;
        }
        localStorage.removeItem("pendingOrderBidsId");
        Swal.fire({
          icon: "success",
          title: "Order Cancelled",
          text: response.data.message,
          confirmButtonColor: "#097d43",
        }).then(() => navigate("/customer/orders"));
      })
      .catch(() => {
        setCancelling(false);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to cancel order." });
      });
  };

  const subTotal = Number(order?.subTotal || 0);
  const discount = Number(order?.discount || 0);
  const deliveryCharge = Number(
    selectedBid?.quoted_charge ?? job?.final_delivery_charge ?? 0,
  );
  const grandTotal = Number(subTotal) - Number(discount) + Number(deliveryCharge);

  return (
    <UserMasterLayout>
      <Container fluid className="cjb-container">
        <div className="cjb-header">
          <div className="cjb-icon-wrapper">
            <FiTruck size={28} />
          </div>
          <div>
            <h2 className="cjb-title">
              {driverSelected ? "Driver Assigned" : "Waiting for Delivery Driver"}
            </h2>
            <p className="cjb-subtitle">
              {job?.total_weight ? `Order weight: ${job.total_weight} kg — ` : ""}
              {driverSelected
                ? "A driver has been selected — complete payment to confirm your order"
                : "Waiting for drivers to place their bids — select one below"}
            </p>
          </div>
        </div>

        {loading ? (
          <>
            <Card className="cjb-bid-card" style={{ marginBottom: 16 }}>
              <Card.Body>
                <SkeletonBlock width="180px" height="14px" borderRadius="4px" />
                <div style={{ marginTop: 12 }}>
                  {[1, 2].map((i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                      <SkeletonBlock width="55%" height="13px" borderRadius="4px" />
                      <SkeletonBlock width="70px" height="13px" borderRadius="4px" />
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
            {[1, 2].map((i) => (
              <Card key={i} className="cjb-bid-card" style={{ marginBottom: 16 }}>
                <Card.Body>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <SkeletonBlock width="48px" height="48px" borderRadius="50%" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SkeletonBlock width="140px" height="14px" borderRadius="4px" />
                      <SkeletonBlock width="100px" height="12px" borderRadius="4px" className="skel-mt-sm" />
                    </div>
                    <SkeletonBlock width="90px" height="36px" borderRadius="8px" />
                  </div>
                </Card.Body>
              </Card>
            ))}
          </>
        ) : (
          <>
            {/* ── NEW: Order Details — products, weight, shipping address.
                Hamesha visible rehta hai taake customer bids select karte
                waqt apna order bhool na jaye ── */}
            {order && (
              <Card className="cjb-bid-card" style={{ marginBottom: 16 }}>
                <Card.Body>
                  <h6 style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, color: "#097d43", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    <FiPackage size={14} /> Order #{order.id} Details
                  </h6>

                  {order.kmcarts?.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      {order.kmcarts.map((cart, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 13.5,
                            padding: "6px 0",
                            borderBottom: i < order.kmcarts.length - 1 ? "1px solid #f0f0f0" : "none",
                          }}
                        >
                          <span>
                            {cart.kmproduct?.name}{" "}
                            <span style={{ color: "#8a9088", fontSize: 12 }}>
                              × {cart.quantity} {cart.kmproduct?.unit}
                            </span>
                          </span>
                          <span style={{ fontWeight: 600, color: "#097d43" }}>
                            Rs. {(cart.amountPerPiece * cart.quantity)?.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px", fontSize: 13, color: "#6c757d", borderTop: "1px solid #f0f0f0", paddingTop: 10 }}>
                    {job?.total_weight && (
                      <span>
                        <strong style={{ color: "#333" }}>Total Weight:</strong> {job.total_weight} kg
                      </span>
                    )}
                    {order.shippingAddress && (
                      <span>
                        <strong style={{ color: "#333" }}>Delivering To:</strong> {order.shippingAddress}
                      </span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {driverSelected ? (
              /* ════ Compact driver card — full summary ab Modal mein hai ════ */
              <Card className="cjb-bid-card">
                <Card.Body>
                  {selectedBid?.kmdeliveryman && (
                    <div className="cjb-driver-info" style={{ marginBottom: 12 }}>
                      {selectedBid.kmdeliveryman?.image ? (
                        <img
                          src={`${baseUrlForFiles}/${selectedBid.kmdeliveryman.image}`}
                          alt={selectedBid.kmdeliveryman?.name}
                          className="cjb-driver-avatar"
                        />
                      ) : (
                        <div className="cjb-driver-avatar-placeholder">
                          {selectedBid.kmdeliveryman?.name?.charAt(0)}
                        </div>
                      )}
                      <div>
                        <h5 className="cjb-driver-name">
                          <FiUser size={13} className="me-1" />
                          {selectedBid.kmdeliveryman?.name}
                        </h5>
                        <div className="cjb-driver-meta">
                          <span>
                            <FiPackage size={13} /> {selectedBid.kmdeliveryman?.vechile_type || "—"}
                            {selectedBid.kmdeliveryman?.vehicle_number ? ` (${selectedBid.kmdeliveryman.vehicle_number})` : ""}
                          </span>
                          {selectedBid.kmdeliveryman?.phone && (
                            <span>
                              <FiPhone size={13} /> {selectedBid.kmdeliveryman.phone}
                            </span>
                          )}
                          {(selectedBid.kmdeliveryman?.min_capacity_kg || selectedBid.kmdeliveryman?.max_capacity_kg) && (
                            <span>
                              <FiTruck size={13} /> Capacity: {selectedBid.kmdeliveryman?.min_capacity_kg}–{selectedBid.kmdeliveryman?.max_capacity_kg} kg
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <Button
                    className="outline-gradient"
                    variant="outline"
                    size="small"
                    icon={<FiDollarSign />}
                    iconPosition="left"
                    onClick={() => setShowSummaryModal(true)}
                  >
                    View Order Summary
                  </Button>
                </Card.Body>
              </Card>
            ) : bids.length === 0 ? (
              <Card className="cjb-empty-card">
                <Card.Body>
                  <FiClock size={32} style={{ marginBottom: 10, opacity: 0.5 }} />
                  <p className="cjb-empty-text">
                    No bids yet. Drivers are reviewing your order and will place their bids soon. Please check back later.
                  </p>
                </Card.Body>
              </Card>
            ) : (
              bids.map((bid) => (
                <Card key={bid.id} className="cjb-bid-card">
                  <Card.Body>
                    <div className="cjb-bid-layout">
                      <div className="cjb-driver-info">
                        {bid.kmdeliveryman?.image ? (
                          <img
                            src={`${baseUrlForFiles}/${bid.kmdeliveryman.image}`}
                            alt={bid.kmdeliveryman?.name}
                            className="cjb-driver-avatar"
                          />
                        ) : (
                          <div className="cjb-driver-avatar-placeholder">
                            {bid.kmdeliveryman?.name?.charAt(0)}
                          </div>
                        )}
                        <div>
                          <h5 className="cjb-driver-name">{bid.kmdeliveryman?.name}</h5>
                          <div className="cjb-driver-meta">
                            <span>
                              <FiPackage size={13} /> {bid.kmdeliveryman?.vechile_type || "—"}
                              {bid.kmdeliveryman?.vehicle_number ? ` (${bid.kmdeliveryman.vehicle_number})` : ""}
                            </span>
                            {bid.kmdeliveryman?.phone && (
                              <span>
                                <FiPhone size={13} /> {bid.kmdeliveryman.phone}
                              </span>
                            )}
                            {(bid.kmdeliveryman?.min_capacity_kg || bid.kmdeliveryman?.max_capacity_kg) && (
                              <span>
                                <FiTruck size={13} /> Capacity: {bid.kmdeliveryman?.min_capacity_kg}–{bid.kmdeliveryman?.max_capacity_kg} kg
                              </span>
                            )}
                            <span>
                              <FiTag size={13} /> Rs. {Number(bid.quoted_charge).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="gradient"
                        icon={<FiCheckCircle />}
                        iconPosition="left"
                        onClick={() => selectDriverBid(bid)}
                        disabled={selectingId === bid.id}
                      >
                        {selectingId === bid.id ? "Selecting..." : "Select This Driver"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))
            )}

            {/* ── neeche hamesha available: driver select ho chuka ho to
                Pay Now + Cancel Order dono saath, warna sirf Cancel Order ── */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              {driverSelected && (
                <Button
                  variant="gradient"
                  size="medium"
                  icon={<FiCreditCard />}
                  iconPosition="left"
                  onClick={payForOrder}
                  disabled={payLoading}
                >
                  {payLoading ? "Redirecting..." : "Pay Now"}
                </Button>
              )}
              {driverSelected && (
                <Button
                  variant="outline"
                  size="medium"
                  icon={<FiDollarSign />}
                  iconPosition="left"
                  onClick={openBankTransferModal}
                  disabled={payLoading}
                >
                  Pay via Bank Transfer
                </Button>
              )}
              <Button
                className="outline-gradient"
                variant="outline"
                size="medium"
                icon={<FiXCircle />}
                iconPosition="left"
                onClick={handleCancelOrder}
                disabled={cancelling}
                style={{ borderColor: "#d33", color: "#d33" }}
              >
                {cancelling ? "Cancelling..." : "Cancel Order"}
              </Button>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            ORDER SUMMARY MODAL — driver select hote hi khud khulta
            hai, ya "View Order Summary" se dobara khol saktay hain.
            Andar Pay Now + Cancel Order dono buttons hain taake
            customer summary dekhte hi seedha action le sake.
        ════════════════════════════════════════════════════════ */}
        <Modal
          show={showSummaryModal}
          onHide={() => setShowSummaryModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 18 }}>
              <FiPackage className="me-2" /> Order Summary
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedBid?.kmdeliveryman && (
              <div className="cjb-driver-info" style={{ marginBottom: 16 }}>
                {selectedBid.kmdeliveryman?.image ? (
                  <img
                    src={`${baseUrlForFiles}/${selectedBid.kmdeliveryman.image}`}
                    alt={selectedBid.kmdeliveryman?.name}
                    className="cjb-driver-avatar"
                  />
                ) : (
                  <div className="cjb-driver-avatar-placeholder">
                    {selectedBid.kmdeliveryman?.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <h5 className="cjb-driver-name">
                    <FiUser size={13} className="me-1" />
                    {selectedBid.kmdeliveryman?.name}
                  </h5>
                  <div className="cjb-driver-meta">
                    <span>
                      <FiPackage size={13} /> {selectedBid.kmdeliveryman?.vechile_type || "—"}
                      {selectedBid.kmdeliveryman?.vehicle_number ? ` (${selectedBid.kmdeliveryman.vehicle_number})` : ""}
                    </span>
                    {selectedBid.kmdeliveryman?.phone && (
                      <span>
                        <FiPhone size={13} /> {selectedBid.kmdeliveryman.phone}
                      </span>
                    )}
                    {(selectedBid.kmdeliveryman?.min_capacity_kg || selectedBid.kmdeliveryman?.max_capacity_kg) && (
                      <span>
                        <FiTruck size={13} /> Capacity: {selectedBid.kmdeliveryman?.min_capacity_kg}–{selectedBid.kmdeliveryman?.max_capacity_kg} kg
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  padding: "6px 0",
                }}
              >
                <span>Subtotal</span>
                <span>Rs. {subTotal.toLocaleString()}</span>
              </div>
              {discount > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 14,
                    padding: "6px 0",
                    color: "#097d43",
                  }}
                >
                  <span>Discount</span>
                  <span>- Rs. {discount.toLocaleString()}</span>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  padding: "6px 0",
                }}
              >
                <span>
                  <FiDollarSign size={13} className="me-1" /> Delivery Charge (COD)
                </span>
                <span>Rs. {deliveryCharge.toLocaleString()}</span>
              </div>
              <hr className="summary-divider" />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "bold",
                  fontSize: 16,
                  padding: "6px 0",
                  color: "#097d43",
                }}
              >
                <span>Total</span>
                <span>Rs. {grandTotal.toLocaleString()}</span>
              </div>
              <p className="km-delivery-cod-note">
                Product amount already charged online. Delivery charge (Rs.{" "}
                {deliveryCharge.toLocaleString()}) is cash on delivery — pay it directly
                to the driver.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ gap: 8 }}>
            <Button
              className="outline-gradient"
              variant="outline"
              size="medium"
              icon={<FiXCircle />}
              iconPosition="left"
              onClick={handleCancelOrder}
              disabled={cancelling}
              style={{ borderColor: "#d33", color: "#d33" }}
            >
              {cancelling ? "Cancelling..." : "Cancel Order"}
            </Button>
            <Button
              variant="gradient"
              size="medium"
              icon={<FiCreditCard />}
              iconPosition="left"
              onClick={payForOrder}
              disabled={payLoading}
            >
              {payLoading ? "Redirecting..." : "Pay Now"}
            </Button>
            <Button
              variant="outline"
              size="medium"
              icon={<FiDollarSign />}
              iconPosition="left"
              onClick={openBankTransferModal}
              disabled={payLoading}
            >
              Pay via Bank Transfer
            </Button>
          </Modal.Footer>
        </Modal>

        {/* NEW: Bank Transfer Payment modal (Module 3) */}
        <Modal show={showBankTransferModal} onHide={() => setShowBankTransferModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Pay via Bank Transfer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingBankAccount ? (
              <p>Loading bank account details...</p>
            ) : adminBankAccount ? (
              <>
                <div style={{ background: "#f4f6f9", padding: "12px 16px", borderRadius: "8px", marginBottom: "16px" }}>
                  <p style={{ margin: 0, fontSize: "14px" }}><strong>Bank:</strong> {adminBankAccount.bankname}</p>
                  <p style={{ margin: 0, fontSize: "14px" }}><strong>Account Title:</strong> {adminBankAccount.accounttitle}</p>
                  <p style={{ margin: 0, fontSize: "14px" }}><strong>Account Number:</strong> {adminBankAccount.accountnumber}</p>
                </div>
                <p style={{ fontSize: "13px", color: "#6b7280" }}>
                  Please transfer the order amount to the account above, then upload your
                  receipt and enter the amount you transferred below.
                </p>
                <div style={{ marginBottom: "12px" }}>
                  <label style={{ fontSize: "13px", fontWeight: 600 }}>Amount Transferred (Rs.)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={transferredAmount}
                    onChange={(e) => setTransferredAmount(e.target.value)}
                    placeholder="Enter the amount you transferred"
                  />
                </div>
                <div>
                  <label style={{ fontSize: "13px", fontWeight: 600 }}>Transfer Receipt</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setReceiptImage(e.target.files[0])}
                  />
                </div>
              </>
            ) : (
              <p>Bank transfer is not available right now.</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={() => setShowBankTransferModal(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              onClick={submitBankTransferReceipt}
              disabled={submittingReceipt || loadingBankAccount || !adminBankAccount}
            >
              {submittingReceipt ? "Submitting..." : "Submit Receipt"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </UserMasterLayout>
  );
};

export default CustomerOrderBids;
